/**
 * STUFE V4G/V4H — „Unterweisungsunterlage mit Unterweisungsnachweis" (Tür 1).
 *
 * V4H-Korrektur: verbindliche Produktreihenfolge (abschließend):
 * 1. Kopf und Thema · 2. Betriebsanweisung · 3. Im Notfall · 4. Erste Hilfe ·
 * 5. Unterweisung · 6. Visuelle Lernhilfen · 7. Persönliche Schutzausrüstung ·
 * 8. Sicherheitssymbole · 9. Richtig und falsch · 10. Unterweisungsnachweis
 * (eigene A4-Seite, exakt 6 Beschäftigte) · 11. Unterweisender · 12. Fußbereich.
 *
 * Single Source of Truth: bestehender Print-Datenendpunkt
 * GET /api/billing/one-time-print-data/{token}?attempt=… (eingefrorener
 * Einmalkauf-Record inkl. `visual_objects`). Keine zweite Quelle, keine KI,
 * keine neuen Texte/Bilder; Fachtexte VERBATIM, Labels sind Bestandslabels.
 *
 * Wiederverwendete Bestandskomponenten (nur importiert, unverändert):
 * VisualObjects (+ Registry: LearningFocusRenderer, HazardMarkersRenderer,
 * PsaSummaryRenderer, SafetySymbolsRenderer), RichtigFalschRenderer,
 * buildRichtigFalsch, Lehrbild-Runtime. Die renderer-internen Titelzeilen
 * werden NUR IN DIESEM DOKUMENT per scoped CSS ausgeblendet (die exakten
 * V4H-Hauptüberschriften ersetzen sie); Richtig-/Falsch-Kennzeichnungen
 * bleiben sichtbar.
 *
 * Chromium-Warte-Vertrag: Warteanker `unterweisungsnachweis-page` erst nach
 * (a) geladenen Print-Daten und (b) abgeschlossenem initialem
 * Lehrbild-Manifest-Abruf. Fehlerzustand ohne Anker (kontrollierter Abbruch).
 *
 * Optionaler Firmenname: nur anzeigen, wenn im gelieferten Datensatz
 * vorhanden; sonst entfällt die Zeile vollständig (kein Platzhalter).
 */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { API } from "@/lib/api";
import { VisualObjects } from "@/components/VisualObjects";
import { RichtigFalschRenderer } from "@/vsi/renderers";
import { buildRichtigFalsch } from "@/vsi/richtigFalsch";
import { setActiveLehrbildWorld, setLehrbildPrintKontext, _debugGetManifestMeta } from "@/vsi/lehrbild";

const DOKUMENT_VERSION = "1.0";
const MITARBEITER_ZEILEN = 6;

function AbschnittTitel({ children }) {
  return (
    <h2 className="font-heading text-lg font-bold text-slate-900 border-b-2 border-slate-900 pb-1.5">
      {children}
    </h2>
  );
}

function BaListe({ label, items, testId }) {
  const list = (Array.isArray(items) ? items : []).filter(
    (t) => typeof t === "string" && t.trim()
  );
  if (list.length === 0) return null;
  return (
    <div className="mt-5" data-testid={testId}>
      <h3 className="font-heading text-sm font-semibold text-slate-900">{label}</h3>
      <ul className="mt-2 space-y-1.5 list-disc pl-5">
        {list.map((t, i) => (
          <li key={i} className="text-sm text-slate-700 leading-relaxed">
            {t}
          </li>
        ))}
      </ul>
    </div>
  );
}

function UnterschriftTabelle({ kopf1, zeilen, testIdPrefix }) {
  return (
    <table className="w-full border-collapse text-sm">
      <thead>
        <tr>
          <th className="text-left font-semibold text-slate-900 border-b-2 border-slate-900 pb-2 w-[44%]">
            {kopf1}
          </th>
          <th className="text-left font-semibold text-slate-900 border-b-2 border-slate-900 pb-2 w-[22%]">
            Datum
          </th>
          <th className="text-left font-semibold text-slate-900 border-b-2 border-slate-900 pb-2 w-[34%]">
            Unterschrift
          </th>
        </tr>
      </thead>
      <tbody>
        {Array.from({ length: zeilen }, (_, i) => (
          <tr
            key={i}
            data-testid={
              zeilen === 1
                ? `${testIdPrefix}-zeile`
                : `${testIdPrefix}-zeile-${i + 1}`
            }
          >
            <td className="border-b border-slate-300 h-10">&nbsp;</td>
            <td className="border-b border-slate-300 h-10">&nbsp;</td>
            <td className="border-b border-slate-300 h-10">&nbsp;</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export default function UnterweisungsnachweisDruck() {
  const { token, attempt } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [manifestReady, setManifestReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(
          `${API}/billing/one-time-print-data/${token}?attempt=${encodeURIComponent(attempt || "")}`
        );
        if (!r.ok) throw new Error("print_context_invalid");
        const d = await r.json();
        if (!cancelled) setData(d);
      } catch {
        if (!cancelled) setError(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, attempt]);

  // IP2-D2 · Print-Renderkontext: die UWN-Druckroute ist ein Chromium-
  // Druckprodukt und verwendet daher Print-Derivate (sonst Master-Fallback).
  // Mount/Unmount-Muster exakt analog IP2-D1C/E1C.
  useEffect(() => {
    setLehrbildPrintKontext(true);
    return () => setLehrbildPrintKontext(false);
  }, []);

  useEffect(() => {
    if (!data) return undefined;
    const worldId = (data.course || {}).id;
    if (!worldId) {
      setManifestReady(true);
      return undefined;
    }
    setActiveLehrbildWorld(worldId);
    let stopped = false;
    const tick = () => {
      if (stopped) return;
      if (_debugGetManifestMeta(worldId)) {
        setManifestReady(true);
        return;
      }
      setTimeout(tick, 100);
    };
    tick();
    return () => {
      stopped = true;
    };
  }, [data]);

  if (error) {
    return (
      <div
        className="max-w-3xl mx-auto p-10 text-slate-500"
        data-testid="unterweisungsnachweis-error"
      >
        Dieses Dokument ist nicht verfügbar.
      </div>
    );
  }

  if (!data || !manifestReady) return null;

  const course = data.course || {};
  const ba = course.betriebsanweisung || {};
  const blocks = Array.isArray(course.content) ? course.content : [];
  const visualObjects = Array.isArray(course.visual_objects)
    ? course.visual_objects
    : [];
  const byType = (t) => visualObjects.filter((o) => o && o.vlo_type === t);
  const lernhilfenVlos = [...byType("learning_focus"), ...byType("hazard_markers")];
  const psaVlos = byType("psa_summary");
  const symbolVlos = byType("safety_symbols");
  const richtigFalsch = buildRichtigFalsch(course);
  const notfall =
    typeof ba.behavior_in_emergency === "string" && ba.behavior_in_emergency.trim()
      ? ba.behavior_in_emergency.trim()
      : "";
  const ersteHilfe =
    typeof ba.first_aid === "string" && ba.first_aid.trim()
      ? ba.first_aid.trim()
      : "";
  const companyName =
    typeof data.company_name === "string" && data.company_name.trim()
      ? data.company_name.trim()
      : "";
  const erstellungsdatum = new Date().toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });

  return (
    <div
      className="max-w-4xl mx-auto px-10 py-6 font-pdf text-slate-900 unterweisungsnachweis-druck"
      data-testid="unterweisungsnachweis-page"
    >
      <style>{`
        .unterweisungsnachweis-druck {
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
        .unterweisungsnachweis-druck header,
        .unterweisungsnachweis-druck footer,
        .unterweisungsnachweis-druck tr,
        .unterweisungsnachweis-druck li,
        .unterweisungsnachweis-druck [data-testid^="unterweisungsnachweis-inhalt-block-"],
        .unterweisungsnachweis-druck [data-testid^="unterweisungsnachweis-ba-"],
        .unterweisungsnachweis-druck [data-testid="unterweisungsnachweis-abschnitt-im-notfall"],
        .unterweisungsnachweis-druck [data-testid="unterweisungsnachweis-abschnitt-erste-hilfe"],
        .unterweisungsnachweis-druck [data-testid^="hazard-marker-"],
        .unterweisungsnachweis-druck [data-testid^="richtig-falsch-richtig-card-"],
        .unterweisungsnachweis-druck [data-testid^="richtig-falsch-falsch-card-"],
        .unterweisungsnachweis-druck [data-testid="learning-focus"],
        .unterweisungsnachweis-druck [data-testid="psa-summary"] {
          break-inside: avoid;
        }
        .unterweisungsnachweis-druck h2,
        .unterweisungsnachweis-druck h3,
        .unterweisungsnachweis-druck [data-testid="richtig-falsch-richtig"] > div:first-child,
        .unterweisungsnachweis-druck [data-testid="richtig-falsch-falsch"] > div:first-child {
          break-after: avoid;
        }
        /* V4H: renderer-interne Titelzeilen werden durch die verbindlichen
           Hauptüberschriften ersetzt (nur in diesem Dokument ausgeblendet;
           Richtig-/Falsch-Kennzeichnungen bleiben sichtbar). */
        .unterweisungsnachweis-druck [data-testid="psa-summary"] > div:first-child,
        .unterweisungsnachweis-druck [data-testid="hazard-markers"] > div:first-child,
        .unterweisungsnachweis-druck [data-testid="learning-focus"] > div:first-child,
        .unterweisungsnachweis-druck [data-testid="richtig-falsch"] > div:first-child {
          display: none;
        }
        .unterweisungsnachweis-nachweisseite {
          break-before: page;
          page-break-before: always;
        }
      `}</style>

      {/* ===================== 1. KOPF UND THEMA ===================== */}
      <header data-testid="unterweisungsnachweis-dokumentkopf">
        <div className="flex items-start justify-between gap-6">
          <div>
            <div className="text-xs uppercase tracking-[0.25em] text-slate-500">
              Unterweisung
            </div>
            <h1
              className="font-heading text-2xl font-bold text-slate-900 mt-1 break-words"
              data-testid="unterweisungsnachweis-titel"
            >
              {course.title || ""}
            </h1>
            {course.short_description ? (
              <p
                className="text-sm text-slate-600 mt-1"
                data-testid="unterweisungsnachweis-untertitel"
              >
                {course.short_description}
              </p>
            ) : null}
            <div
              className="text-sm text-slate-600 mt-1.5"
              data-testid="unterweisungsnachweis-erstellungsdatum"
            >
              Erstellungsdatum: {erstellungsdatum}
            </div>
            {companyName ? (
              <div
                className="text-sm text-slate-700 mt-1"
                data-testid="unterweisungsnachweis-firmenname"
              >
                {companyName}
              </div>
            ) : null}
          </div>
          <img
            src="/brand/clartext-logo-full-light.svg"
            alt="CLartext"
            className="h-9 w-auto shrink-0"
            data-testid="unterweisungsnachweis-logo"
          />
        </div>
        <div className="border-t-2 border-slate-900 mt-5" />
      </header>

      {/* ===================== 2. BETRIEBSANWEISUNG ===================== */}
      {ba.main_hazards || ba.protective_measures ? (
        <section
          className="mt-10"
          data-testid="unterweisungsnachweis-abschnitt-betriebsanweisung"
        >
          <AbschnittTitel>Betriebsanweisung</AbschnittTitel>
          <BaListe
            label="Hauptgefahren"
            items={ba.main_hazards}
            testId="unterweisungsnachweis-ba-hauptgefahren"
          />
          <BaListe
            label="Schutzmaßnahmen"
            items={ba.protective_measures}
            testId="unterweisungsnachweis-ba-schutzmassnahmen"
          />
        </section>
      ) : null}

      {/* ===================== 3. IM NOTFALL ===================== */}
      {notfall ? (
        <section
          className="mt-10"
          data-testid="unterweisungsnachweis-abschnitt-im-notfall"
        >
          <AbschnittTitel>Im Notfall</AbschnittTitel>
          <p className="mt-4 text-sm text-slate-700 leading-relaxed">{notfall}</p>
        </section>
      ) : null}

      {/* ===================== 4. ERSTE HILFE ===================== */}
      {ersteHilfe ? (
        <section
          className="mt-10"
          data-testid="unterweisungsnachweis-abschnitt-erste-hilfe"
        >
          <AbschnittTitel>Erste Hilfe</AbschnittTitel>
          <p className="mt-4 text-sm text-slate-700 leading-relaxed">
            {ersteHilfe}
          </p>
        </section>
      ) : null}

      {/* ===================== 5. UNTERWEISUNG ===================== */}
      {blocks.length > 0 ? (
        <section
          className="mt-10"
          data-testid="unterweisungsnachweis-abschnitt-unterweisung"
        >
          <AbschnittTitel>Unterweisung</AbschnittTitel>
          <div className="mt-5 space-y-6">
            {blocks.map((b, i) => (
              <div key={i} data-testid={`unterweisungsnachweis-inhalt-block-${i}`}>
                {b.heading ? (
                  <h3 className="font-heading text-sm font-semibold text-slate-900">
                    {i + 1}. {b.heading}
                  </h3>
                ) : null}
                {b.body ? (
                  <p className="mt-2 text-sm text-slate-700 leading-relaxed">
                    {b.body}
                  </p>
                ) : null}
              </div>
            ))}
          </div>
        </section>
      ) : null}

      {/* ===================== 6. VISUELLE LERNHILFEN ===================== */}
      {lernhilfenVlos.length > 0 ? (
        <section
          className="mt-10"
          data-testid="unterweisungsnachweis-abschnitt-lernhilfen"
        >
          <AbschnittTitel>Visuelle Lernhilfen</AbschnittTitel>
          <div className="mt-5 space-y-5">
            <VisualObjects objects={lernhilfenVlos} lang="de" />
          </div>
        </section>
      ) : null}

      {/* ===================== 7. PSA-ZUSAMMENFASSUNG ===================== */}
      {psaVlos.length > 0 ? (
        <section
          className="mt-10"
          data-testid="unterweisungsnachweis-abschnitt-psa"
        >
          <AbschnittTitel>Persönliche Schutzausrüstung</AbschnittTitel>
          <div className="mt-5">
            <VisualObjects objects={psaVlos} lang="de" />
          </div>
        </section>
      ) : null}

      {/* ===================== 8. SICHERHEITSSYMBOLE ===================== */}
      {symbolVlos.length > 0 ? (
        <section
          className="mt-10"
          data-testid="unterweisungsnachweis-abschnitt-symbole"
        >
          <AbschnittTitel>Sicherheitssymbole</AbschnittTitel>
          <div className="mt-5">
            <VisualObjects objects={symbolVlos} lang="de" />
          </div>
        </section>
      ) : null}

      {/* ===================== 9. RICHTIG UND FALSCH ===================== */}
      {richtigFalsch ? (
        <section
          className="mt-10"
          data-testid="unterweisungsnachweis-abschnitt-richtig-falsch"
        >
          <AbschnittTitel>Richtig und falsch</AbschnittTitel>
          <div className="mt-5">
            <RichtigFalschRenderer vlo={richtigFalsch} lang="de" />
          </div>
        </section>
      ) : null}

      {/* ========== 10.–12. UNTERWEISUNGSNACHWEIS (eigene A4-Seite) ========== */}
      <section
        className="unterweisungsnachweis-nachweisseite"
        data-testid="unterweisungsnachweis-nachweisseite"
      >
        <h2
          className="font-heading text-2xl font-bold text-slate-900"
          data-testid="unterweisungsnachweis-nachweis-titel"
        >
          Unterweisungsnachweis
        </h2>
        <div
          className="text-sm text-slate-700 mt-2"
          data-testid="unterweisungsnachweis-nachweis-thema"
        >
          {course.title || ""}
        </div>
        <div
          className="text-sm text-slate-600 mt-1"
          data-testid="unterweisungsnachweis-nachweis-datum"
        >
          Erstellungsdatum: {erstellungsdatum}
        </div>
        {companyName ? (
          <div
            className="text-sm text-slate-700 mt-1"
            data-testid="unterweisungsnachweis-nachweis-firmenname"
          >
            {companyName}
          </div>
        ) : null}
        <div className="border-t-2 border-slate-900 mt-4" />
        <p
          className="text-sm text-slate-700 mt-5"
          data-testid="unterweisungsnachweis-hinweis"
        >
          Die Unterweisung wurde anhand der vorstehenden Unterlagen
          durchgeführt.
        </p>

        {/* 10. Sechs Beschäftigte */}
        <div className="mt-6" data-testid="unterweisungsnachweis-unterschriften">
          <UnterschriftTabelle
            kopf1="Name, Vorname"
            zeilen={MITARBEITER_ZEILEN}
            testIdPrefix="unterweisungsnachweis-mitarbeiter"
          />
        </div>

        {/* 11. Unterweisender */}
        <div className="mt-8" data-testid="unterweisungsnachweis-unterweisender">
          <UnterschriftTabelle
            kopf1="Name Unterweisender"
            zeilen={1}
            testIdPrefix="unterweisungsnachweis-unterweisender"
          />
        </div>

        {/* 12. Fußbereich */}
        <footer
          className="mt-10 pt-4 border-t border-slate-200"
          data-testid="unterweisungsnachweis-fussbereich"
        >
          <div className="flex items-center gap-2 text-xs text-slate-500">
            <span>Erstellt mit CLartext</span>
            <img
              src="/brand/clartext-logo-full-light.svg"
              alt="CLartext"
              className="h-5 w-auto"
              data-testid="unterweisungsnachweis-fusslogo"
            />
            <span data-testid="unterweisungsnachweis-fuss-url">clartext.io</span>
          </div>
          <div className="flex items-center gap-4 text-[10px] text-slate-400 mt-2">
            <span data-testid="unterweisungsnachweis-dokumentversion">
              Dokumentversion {DOKUMENT_VERSION}
            </span>
            <span data-testid="unterweisungsnachweis-fuss-datum">
              Erstellungsdatum: {erstellungsdatum}
            </span>
          </div>
        </footer>
      </section>
    </div>
  );
}
